import React, { useState } from "react";
import { Field, FieldProps, Form, Formik, FormikHelpers } from "formik";
import Input from "../Input/Input";
import HideIcon from "../../assets/svg/hide-icon";
import Button from "../Button/Button";
import LeftArrowIcon from "../../assets/svg/left-arrow-icon";
import CheckIcon from "../../assets/svg/check-icon";
import PreviewIcon from "../../assets/svg/preview-icon";

interface ChangePasswordValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword = ({
  setShowScreen,
  onSubmit,
}: {
  setShowScreen: (
    screen: "profile" | "change-password" | "edit-profile",
  ) => void;
  onSubmit: (oldPassword: string, newPassword: string) => Promise<void>;
}) => {
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    capital: false,
    special: false,
  });
  const initialValues: ChangePasswordValues = {
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const handleSubmit = async (
    values: ChangePasswordValues,
    { setSubmitting }: FormikHelpers<ChangePasswordValues>,
  ) => {
    try {
      if (!values.oldPassword || !values.newPassword) {
        throw new Error("Please fill in all required fields");
      }
      await onSubmit(values.oldPassword, values.newPassword);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const validatePassword = (value: string) => {
    const validation = {
      length: value.length >= 8,
      capital: /[A-Z]/.test(value),
      special: /[!@#$%^&*]/.test(value),
    };
    setPasswordValidation(validation);
    return validation.length && validation.capital && validation.special
      ? undefined
      : "Invalid password";
  };

  const handlePasswordChange = (value: string) => {
    setPasswordValidation({
      length: value.length >= 8,
      capital: /[A-Z]/.test(value),
      special: /[!@#$%^&*]/.test(value),
    });
  };
  return (
    <div className="flex flex-col gap-4 p-3">
      {" "}
      <div className="flex gap-2 justify-start">
        <Button
          innerText="Go back"
          Icon={LeftArrowIcon}
          onClick={() => setShowScreen("profile")}
          iconLeft
          buttonContainerClass="self-start"
          iconClass="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk"
          innerTextClass="text-xs text-heading-text dark:text-heading-text-dk font-medium"
        />
      </div>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ isSubmitting, values }) => (
          <Form className="flex flex-col gap-4">
            <Field
              name="oldPassword"
              validate={(value: string) => !value && "Old password is required"}
            >
              {({ field, meta }: FieldProps) => (
                <Input
                  label="Enter old password"
                  type={showPassword.old ? "text" : "password"}
                  placeholder="Enter old password"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handlePasswordChange(e.target.value);
                  }}
                  error={meta.touched && !!meta.error}
                  errorMessage={meta.error}
                  rightIcon={showPassword.old ? PreviewIcon : HideIcon}
                  onRightIconClick={() =>
                    setShowPassword((prev) => ({ ...prev, old: !prev.old }))
                  }
                  labelClassName="text-sm text-verba-field-label dark:text-verba-field-label-dk font-normal"
                  secondaryContainerClassName="w-full p-2 border border-quaternary-gray-lt bg-pure-white dark:bg-pure-white-dk rounded-lg text-primary-blue-dk text-sm focus:outline-none dark:text-primary-gray-lightest dark:border-none"
                />
              )}
            </Field>

            <Field name="newPassword" validate={validatePassword}>
              {({ field, meta }: FieldProps) => (
                <>
                  <Input
                    label="Enter new password"
                    type={showPassword.new ? "text" : "password"}
                    placeholder="Enter new password"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handlePasswordChange(e.target.value);
                    }}
                    error={meta.touched && !!meta.error}
                    rightIcon={showPassword.new ? PreviewIcon : HideIcon}
                    onRightIconClick={() =>
                      setShowPassword((prev) => ({ ...prev, new: !prev.new }))
                    }
                    labelClassName="text-sm text-verba-field-label dark:text-verba-field-label-dk font-normal"
                    secondaryContainerClassName="w-full p-2 border border-quaternary-gray-lt bg-pure-white dark:bg-pure-white-dk rounded-lg text-primary-blue-dk text-sm focus:outline-none dark:text-primary-gray-lightest dark:border-none"
                  />
                  {field.value && (
                    <div className="text-sm mt-1">
                      <p className="text-verba-chat dark:text-verba-chat-dk font-normal">
                        Password must contain:
                      </p>
                      <ul className="list-disc ml-5 text-data dark:text-data-dk font-normal">
                        <li>
                          <div className="flex items-center gap-2">
                            At least 8 characters
                            {passwordValidation.length && (
                              <CheckIcon className="w-3.5 h-3.5 text-verba-green-success dark:text-verba-green-success-dk" />
                            )}
                          </div>
                        </li>
                        <li>
                          <div className="flex items-center gap-2">
                            At least 1 capital letter
                            {passwordValidation.capital && (
                              <CheckIcon className="w-3.5 h-3.5 text-verba-green-success dark:text-verba-green-success-dk" />
                            )}
                          </div>
                        </li>
                        <li>
                          <div className="flex items-center gap-2">
                            At least 1 special character (!@#$%^&*)
                            {passwordValidation.special && (
                              <CheckIcon className="w-3.5 h-3.5 text-verba-green-success dark:text-verba-green-success-dk" />
                            )}
                          </div>
                        </li>
                      </ul>
                    </div>
                  )}
                </>
              )}
            </Field>

            <Field
              name="confirmPassword"
              validate={(value: string) => {
                if (!value) return "Confirm password is required";
                if (value !== values.newPassword)
                  return "Passwords do not match";
              }}
            >
              {({ field, meta }: FieldProps) => (
                <Input
                  label="Confirm password"
                  type={showPassword.confirm ? "text" : "password"}
                  placeholder="Confirm password"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handlePasswordChange(e.target.value);
                  }}
                  error={meta.touched && !!meta.error}
                  errorMessage={meta.error}
                  rightIcon={showPassword.confirm ? HideIcon : PreviewIcon}
                  onRightIconClick={() =>
                    setShowPassword((prev) => ({
                      ...prev,
                      confirm: !prev.confirm,
                    }))
                  }
                  labelClassName="text-sm text-verba-field-label dark:text-verba-field-label-dk font-normal"
                  secondaryContainerClassName="w-full p-2 border border-quaternary-gray-lt bg-pure-white dark:bg-pure-white-dk rounded-lg text-primary-blue-dk text-sm focus:outline-none dark:text-primary-gray-lightest dark:border-none"
                />
              )}
            </Field>

            <div className="flex justify-end gap-2">
              <Button
                innerText="Cancel"
                buttonContainerClass="py-1 px-7.5 w-full border border-verba-indigo dark:border-verba-indigo-dk rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
                onClick={() => setShowScreen("profile")}
              />
              <Button
                innerText="Set new password"
                type="submit"
                disabled={
                  isSubmitting ||
                  !values.oldPassword ||
                  !values.newPassword ||
                  !values.confirmPassword
                }
                isLoading={isSubmitting}
                buttonContainerClass="py-1 px-4 min-w-[154px] rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
                innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ChangePassword;
